<template>
  <div
    ref="actionMenuContainer"
    class="asset-module__action-menu-container"
  >
    <!--
      The action menu dropdown needs to be positioned above everything else on the page,
      but neither the ActionMenu and NebulaDropdown let us move the dropdown around dynamically.
      Instead we're positioning an entire NebulaDropdown, and then moving only the trigger
      button back where it should be in the DOM.

      If in the future NebulaDropdown supports fixed positioning, this can be removed
      and replaced with a normal ActionMenu.
    -->
    <EditorMenu
      v-if="isDisplayed && triggerButtonParentEl && asset"
      :module-id="moduleData.id"
      :trigger-element="$refs.actionMenuContainer"
      :trigger-padding="6"
      :positions="isRtl ? ['bottomRight'] : ['bottomLeft']"
      append-to-editor
      :box-name="canvasName === 'notes' ? 'notes' : 'editor'"
      :scale="scale"
    >
      <NebulaDropdown
        class="nebula-card__action-menu module-action-menu__dropdown"
        ref="actionMenuDropdown"
        :button-id="dropdownTriggerId"
      >
        <template
          #trigger="{
            toggleDropdown,
            checkArrowKeyOnTrigger,
            checkArrowKeyAfterTrigger,
            isOpen,
          }"
        >
          <NebulaButton
            v-appendTo="triggerButtonParentEl"
            :id="dropdownTriggerId"
            class="asset-module__action-menu-trigger"
            icon-left="ellipses-vertical"
            shape="pill"
            size="s"
            type="ghost"
            :aria-label="$t('Open actions menu')"
            @click="toggleDropdown"
            @keydown="checkArrowKeyOnTrigger"
            @keyup="checkArrowKeyAfterTrigger"
            :aria-expanded="isOpen ? 'true' : 'false'"
          />
        </template>

        <!-- These props and listeners come from the actionMenu mixin -->
        <template #content>
          <template v-if="isChannel && !userIsStudent">
            <NebulaDropdownItem
              v-if="isSubscribedChannel"
              icon="bookmark"
              :text="$t('Remove from My Channels')"
              @dropdown-item-clicked="unsubscribe"
            />
            <NebulaDropdownItem
              v-else
              icon="bookmark"
              :text="$t('Add to My Channels')"
              @dropdown-item-clicked="subscribe"
            />
          </template>

          <template v-if="showQuickListOptions">
            <NebulaDropdownItem
              v-if="isInQL"
              icon="circle-minus"
              :text="$t('Remove from Favorites')"
              @dropdown-item-clicked="onRemoveFromQuicklist"
            />
            <NebulaDropdownItem
              v-else
              icon="circle-plus"
              :text="$t('Add to Favorites')"
              @dropdown-item-clicked="onAddToQuicklist"
            />
          </template>

          <NebulaDropdownItem
            v-if="hasAssign"
            :link="assignHref"
            icon="arrow-right-circle"
            :text="$t('Assign')"
          />

          <NebulaDropdownItem
            icon="share"
            :text="$t('Share')"
            @dropdown-item-clicked="openShareModal"
          />

          <NebulaDropdownItem
            icon="drive"
            :text="$t('Save')"
            @dropdown-item-clicked="openMyContentModal"
          />
        </template>
      </NebulaDropdown>
    </EditorMenu>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import { NebulaButton, NebulaDropdown, NebulaDropdownItem } from '@discoveryedu/nebula-components';
import EditorMenu from '@/components/EditorMenu.vue';
import appendTo from '@/directives/appendTo';
import actionMenu from '@/mixins/actionMenu';
import { bus } from '@/lib/eventBus';
import {
  ASSET_SUBTYPE_LESSON,
  ASSET_SUBTYPE_STUDIO_QUIZ,
  ASSET_TYPE_INVESTIGATION,
  ASSET_TYPE_TECHNOLOGY_ENHANCED_ASSESSMENT,
} from '@/lib/constants';
import {
  useUserStore,
} from '@/stores';

export default {
  name: 'ModuleActionMenu',
  components: {
    EditorMenu,
    NebulaButton,
    NebulaDropdown,
    NebulaDropdownItem,
  },
  directives: {
    appendTo,
  },
  mixins: [actionMenu],
  props: {
    asset: {
      type: Object,
      required: true,
    },
    canvasName: {
      type: String,
      default: null,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    scale: {
      type: Number,
      default: 1,
    },
    isDisplayed: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      triggerButtonParentEl: null,
    };
  },
  methods: {
    onPageNavigationStart() {
      const dropdown = this.$refs?.actionMenuDropdown;
      if (dropdown) {
        dropdown.dropdownOpen = false;
      }
    },
  },
  computed: {
    ...mapState(useUserStore, [
      'isRtl',
    ]),
    /* These are adapted from the ActionMenu in platform-components */
    assetTypeFriendly() {
      if (this.asset.type && this.asset.type.name) {
        return this.asset.type.name;
      }
      if (this.asset.type_name) {
        return this.asset.type_name;
      }
      return null;
    },
    assetSubtypeId() {
      return this.asset.subtype?.id || this.asset.sub_type_id || this.asset.subtype_id;
    },
    assetTypeId() {
      return this.asset.type?.id || this.asset.type_id;
    },
    assignHref() {
      const backUrl = window.location.href;
      return `${this.domains.appDomain}/assign/${this.asset.id}?back=${encodeURIComponent(backUrl)}`;
    },
    dropdownTriggerId() {
      return `action-menu--${this.moduleData.id}`;
    },
    hasAssign() {
      return !this.isChannel
        && !this.isInvestigation
        && !this.userIsStudent;
    },
    isChannel() {
      return this.assetTypeFriendly === 'Channel';
    },
    isInvestigation() {
      return this.assetTypeId === ASSET_TYPE_INVESTIGATION;
    },
    isInQL() {
      // eslint-disable-next-line max-len
      return this.quicklistIds.find((qLGuid) => qLGuid.toLowerCase() === this.asset.id.toLowerCase());
    },
    isSubscribedChannel() {
      return this.channelIds.find((sCGuid) => sCGuid.toLowerCase() === this.asset.id.toLowerCase());
    },
    showQuickListOptions() {
      if (this.assetTypeId === ASSET_TYPE_TECHNOLOGY_ENHANCED_ASSESSMENT) {
        // For TEAs, only Studio Quizzes and Lessons can be added to the QuickList
        return this.assetSubtypeId === ASSET_SUBTYPE_LESSON
          || this.assetSubtypeId === ASSET_SUBTYPE_STUDIO_QUIZ;
      }

      return true;
    },
  },
  mounted() {
    this.triggerButtonParentEl = this.$refs.actionMenuContainer.closest('.asset-module')
      .querySelector('.nebula-card__content .nebula-card__header');
    bus.on('editor-canvas-carousel:start-page-change-animation', this.onPageNavigationStart);
  },
  beforeUnmount() {
    bus.off('editor-canvas-carousel:start-page-change-animation', this.onPageNavigationStart);
  },
};
</script>

<style lang="stylus">
.asset-module__action-menu-container {
  // The trigger button is always added at the end of the .nebula-card__header
  // container, but we want our bounding box to be snapped to the end
  // so that the action menu aligns with the right side of the trigger button
  align-self: stretch;
  order: 5;
}

.module-action-menu {
  &__dropdown {
    .nebula-dropdown__content {
      inset-block-start: 0;
      inset-inline-start: 0;
      position: relative;
    }
  }
}
</style>
